<template>
  <div class="py-6">
    <div class="flex items-start justify-between">
      <LabelValue :label="t('Reviews.agency.view.title.label').toString()">
        <span data-testid="agency-title"> {{ agency.title }}</span>
      </LabelValue>
      <OpenAgencyProfile class="shrink-0" :slug="agency.slug" />
    </div>
    <div class="grid grid-cols-5">
      <LabelValue class="col-span-1 laptop:col-span-2" :label="t('Reviews.agency.view.logo.label').toString()">
        <ProductLogo v-if="agency.logo" :logo-url="agency.logo.url" size="large" data-testid="agency-logo" />
        <span v-else>{{ t('Reviews.agency.view.logo.missing') }}</span>
      </LabelValue>
      <LabelValue class="col-span-1 laptop:col-span-3" :label="t('Reviews.agency.view.hero.label').toString()">
        <div v-if="agency.hero" class="max-w-md">
          <AssetPreview
            data-testid="agency-hero-image"
            :content-type="agency.hero.contentType"
            :disabled="!agency.configFlags.heroActive"
            :name="agency.hero.name"
            :poster-url="agency.heroVideoThumbnail?.url"
            :url="agency.hero.url"
          />
        </div>
        <template v-else>
          <span v-if="agency.configFlags.heroActive">{{ t('Reviews.agency.view.hero.missing') }}</span>
          <span v-else>{{ t('Reviews.agency.view.hero.notActive') }}</span>
        </template>
      </LabelValue>
    </div>
    <LabelValue :label="t('Reviews.agency.view.category.label').toString()" data-testid="agency-category">
      <div class="flex flex-wrap items-center gap-2">
        <ChipBadge v-for="(category, index) in agency.categories" :key="index" rounded color="purple">
          {{ category.title }}
        </ChipBadge>
      </div>
    </LabelValue>
    <LabelValue :label="t('Reviews.agency.view.locations.label').toString()" data-testid="agency-locations">
      <div v-if="agency.remote || locations.length > 0" class="flex flex-wrap items-center gap-2">
        <ChipBadge v-if="agency.remote" rounded>{{ t('Reviews.agency.locations.remoteService') }}</ChipBadge>
        <ChipBadge v-for="(location, index) in locations" :key="index" rounded color="purple">
          {{ location }}
        </ChipBadge>
      </div>
      <span v-else>{{ t('Reviews.agency.view.locations.missing') }}</span>
    </LabelValue>
    <LabelValue :label="t('Reviews.agency.view.description.label').toString()">
      <div class="grid grid-cols-2 gap-x-6" data-testid="agency-short-description">
        <div
          v-for="lang in ALLOWED_LOCALES"
          :key="lang"
          class="flex flex-col items-start gap-y-2"
          :data-testid="'short-description-' + lang"
        >
          <div class="flex w-full items-center gap-x-2">
            <span class="text-body-s-bold text-main">
              {{ t('Reviews.agency.view.description.shortIntro.label') }}
            </span>
            <ChipBadge class="uppercase" rounded>{{ lang }}</ChipBadge>
          </div>
          <span v-if="agency.shortDescriptionTranslationsMap[lang]" :data-testid="'short-description-text-' + lang">{{
            agency.shortDescriptionTranslationsMap[lang]
          }}</span>
          <span v-else class="text-main">{{ t('Reviews.agency.view.shortDescription.missing') }}</span>
        </div>
      </div>
      <Expandable class="mt-24" :max-height="247">
        <div class="grid grid-cols-2 gap-x-6" data-testid="agency-description">
          <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="flex flex-col items-start gap-y-2" :data-testid="'description-' + lang">
            <div class="flex w-full items-center gap-x-2">
              <span class="text-body-s-bold text-main">
                {{ t('Reviews.agency.view.description.full.label') }}
              </span>
              <ChipBadge class="uppercase" rounded>{{ lang }}</ChipBadge>
            </div>
            <div>
              <MarkdownContent
                v-if="agency.descriptionTranslationsMap[lang]"
                :md="agency.descriptionTranslationsMap[lang]"
                :data-testid="'description-text-' + lang"
              />
              <span v-else class="text-main">{{ t('Reviews.agency.view.description.missing') }}</span>
            </div>
          </div>
        </div>
      </Expandable>
    </LabelValue>
    <LabelValue :label="t('Reviews.agency.view.links.label').toString()" data-testid="agency-links">
      <div class="grid grid-cols-2 gap-x-6 gap-y-2">
        <div v-for="lang in ALLOWED_LOCALES" :key="lang">
          <ChipBadge class="mb-4 uppercase" rounded>{{ lang }}</ChipBadge>
          <div class="flex items-center gap-x-2">
            <MdiSvg :path="mdiWeb" :size="24" class="social-link-icon" />
            <a
              v-if="agency.urlTranslationsMap[lang]"
              class="overflow-hidden text-ellipsis whitespace-nowrap"
              :href="agency.urlTranslationsMap[lang]"
              :data-testid="`agency-website-${lang}`"
            >
              {{ agency.linkTextTranslationsMap[lang] || agency.urlTranslationsMap[lang] }}
            </a>
            <span v-else class="text-main">{{ t('Reviews.agency.view.url.missing') }}</span>
          </div>
        </div>
        <div class="flex items-center gap-x-2">
          <MdiSvg :path="mdiLinkedin" :size="24" class="social-link-icon" />
          <a
            v-if="agency.linkedin"
            class="overflow-hidden text-ellipsis whitespace-nowrap"
            :href="agency.linkedin"
            data-testid="agency-linkedin"
          >
            {{ agency.linkedin }}
          </a>
          <span v-else class="text-main">{{ t('Reviews.agency.view.linkedin.missing') }}</span>
        </div>
        <div />
        <div class="flex items-center gap-x-2">
          <MdiSvg :path="iconTwitterX" :size="24" class="social-link-icon" />
          <a
            v-if="agency.twitter"
            class="overflow-hidden text-ellipsis whitespace-nowrap"
            :href="agency.twitter"
            data-testid="agency-twitter"
          >
            {{ agency.twitter }}
          </a>
          <span v-else class="text-main">{{ t('Reviews.agency.view.twitter.missing') }}</span>
        </div>
      </div>
    </LabelValue>
    <ReviewsCtaView :data="agency" />
  </div>
</template>
<script setup lang="ts">
import { mdiLinkedin, mdiWeb } from '@mdi/js'
import { ChipBadge, Expandable, iconTwitterX, MarkdownContent, MdiSvg } from '@ramp106/omrjs-core-ui'
import { ProductLogo } from '@ramp106/omrjs-reviews-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import OpenAgencyProfile from '@/components/ReviewsAgenciesManagements/OpenAgencyProfile.vue'
import ReviewsCtaView from '@/components/ReviewsManagement/ReviewsCtaView.vue'
import type { AgencyData } from '@/gql/reviews'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper.ts'
import AssetPreview from '@/ui/AssetPreview.vue'
import LabelValue from '@/ui/LabelValue.vue'

const props = defineProps<{
  agency: AgencyData
}>()

const { t } = useI18n()

const locations = computed(() => {
  return props.agency.locations.map((location) => location.city || '')
})
</script>
